import { Flex, Table, Tbody, Td, Tfoot, Th, Thead, Tr } from '@chakra-ui/react';
import {
  Column,
  flexRender,
  Table as TanStackTable,
} from '@tanstack/react-table';
import React from 'react';

import { colors } from '~/packages/common-frontend/colors';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { SortIcon } from '~/packages/common-frontend/tables/SortIcon';
import { useSimpleColorMode } from '~/packages/common-frontend/theme/utils';

// Hooks
const useGetFixedStyles = () => {
  const mode = useSimpleColorMode();
  return function getFixedStyles<TData>(column: Column<TData, unknown>) {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left');

    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right');

    const styles = {
      position: isPinned ? 'sticky' : 'relative',
      width: `${column.getSize()}px`,
      minWidth: `${column.getSize()}px`,
      zIndex: isPinned ? 2 : 1,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    } as const;

    if (isLastLeftPinnedColumn) {
      return {
        ...styles,
        borderRight: `1px solid`,
        borderRightColor: mode('gray.100', 'gray.800'),
        boxShadow: '0 0 4px hsl(0deg 0% 76% / 35%)',
        clipPath: 'inset(0px -4px 0px 0px)',
      };
    }
    if (isFirstRightPinnedColumn) {
      return {
        ...styles,
        borderLeft: `1px solid`,
        borderLeftColor: mode('gray.100', 'gray.800'),
        boxShadow: '0 0 4px hsl(0deg 0% 76% / 35%)',
        clipPath: 'inset(0px 0px 0px -4px)',
      };
    }
    return styles;
  };
};

// Exports
type TableHeaderBETAProps<TData extends Record<string, unknown>> = {
  table: TanStackTable<TData>;
};
export function TableHeaderBETA<TData extends Record<string, unknown>>({
  table,
}: TableHeaderBETAProps<TData>) {
  const mode = useSimpleColorMode();
  const getFixedStyles = useGetFixedStyles();

  // At least one column definition must have a specified `header` for the header row to show up
  const hasHeaderRow = table
    .getHeaderGroups()
    .map((group) =>
      group.headers.map((header) => header.column.columnDef.header),
    )
    .flat()
    .some((columnDef) => !!columnDef);
  if (!hasHeaderRow) return null;

  return (
    <Thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <Tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <Th
              key={header.id}
              onClick={header.column.getToggleSortingHandler()}
              background={mode(colors.gray[50], colors.gray[900])}
              cursor={header.column.getCanSort() ? 'pointer' : 'default'}
              padding="1rem"
              colSpan={header.colSpan}
              boxSizing="border-box"
              verticalAlign="top"
              whiteSpace="nowrap"
              {...getFixedStyles(header.column)}
            >
              <Typography inline variant="base" fontWeight="medium">
                <Flex
                  as="span"
                  alignItems="baseline"
                  justifyContent="space-between"
                  gap="0.5rem"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  <SortIcon
                    columnCanSort={header.column.getCanSort()}
                    sorted={header.column.getIsSorted()}
                  />
                </Flex>
              </Typography>
            </Th>
          ))}
        </Tr>
      ))}
    </Thead>
  );
}
type TableBodyBETAProps<TData extends Record<string, unknown>> = {
  table: TanStackTable<TData>;
  clickableRowFilter?: (row: TData) => boolean;
  onRowClick?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => void;
};
export function TableBodyBETA<TData extends Record<string, unknown>>({
  table,
  clickableRowFilter = () => true,
  onRowClick,
}: TableBodyBETAProps<TData>) {
  const mode = useSimpleColorMode();
  const getFixedStyles = useGetFixedStyles();
  return (
    <Tbody>
      {table.getRowModel().rows.map((row) => {
        const isRowClickable = !!onRowClick && clickableRowFilter(row.original);
        return (
          <Tr
            key={row.id}
            onClick={
              isRowClickable
                ? (event) => onRowClick(row.original, event)
                : undefined
            }
            sx={
              isRowClickable
                ? {
                    cursor: 'pointer',
                    '&:hover td': { background: mode('gray.50', 'gray.800') },
                    '&:active td': { background: mode('gray.100', 'gray.700') },
                  }
                : undefined
            }
          >
            {row.getVisibleCells().map((cell) => (
              <Td
                key={cell.id}
                backgroundColor={mode('white', 'gray.950')}
                padding="1rem"
                boxSizing="border-box"
                {...getFixedStyles(cell.column)}
              >
                <Typography inline variant="base-muted">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Typography>
              </Td>
            ))}
          </Tr>
        );
      })}
    </Tbody>
  );
}

type TableFooterBETAProps<TData extends Record<string, unknown>> = {
  table: TanStackTable<TData>;
};
export function TableFooterBETA<TData extends Record<string, unknown>>({
  table,
}: TableFooterBETAProps<TData>) {
  const mode = useSimpleColorMode();
  const getFixedStyles = useGetFixedStyles();

  // At least one column definition must have a specified `footer` for the footer row to show up
  const hasFooterRow = table
    .getFooterGroups()
    .map((group) =>
      group.headers.map((header) => header.column.columnDef.footer),
    )
    .flat()
    .some((columnDef) => !!columnDef);
  if (!hasFooterRow) return null;

  return (
    <Tfoot>
      {table.getFooterGroups().map((footerGroup) => (
        <Tr key={footerGroup.id}>
          {footerGroup.headers.map((header) => (
            <Th
              key={header.id}
              background={mode('v2-aurora.50', 'v2-aurora.900')}
              padding="1rem"
              colSpan={header.colSpan}
              verticalAlign="top"
              whiteSpace="nowrap"
              boxSizing="border-box"
              {...getFixedStyles(header.column)}
            >
              <Typography inline variant="base" fontWeight="medium">
                <Flex
                  as="span"
                  alignItems="baseline"
                  justifyContent="space-between"
                  gap="0.5rem"
                >
                  {flexRender(
                    header.column.columnDef.footer,
                    header.getContext(),
                  )}
                </Flex>
              </Typography>
            </Th>
          ))}
        </Tr>
      ))}
    </Tfoot>
  );
}

type TableBETAProps<TData extends Record<string, unknown>> = {
  table: TanStackTable<TData>;
  clickableRowFilter?: TableBodyBETAProps<TData>['clickableRowFilter'];
  onRowClick?: TableBodyBETAProps<TData>['onRowClick'];
};
export function TableBETA<TData extends Record<string, unknown>>({
  table,
  clickableRowFilter,
  onRowClick,
}: TableBETAProps<TData>) {
  const mode = useSimpleColorMode();
  return (
    <Flex
      borderWidth="thin"
      borderColor={mode(colors.gray[100], colors.gray[800])}
      overflowX="auto"
      position="relative"
      width="100%"
    >
      <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <TableHeaderBETA table={table} />
        <TableBodyBETA
          table={table}
          clickableRowFilter={clickableRowFilter}
          onRowClick={onRowClick}
        />
        <TableFooterBETA table={table} />
      </Table>
    </Flex>
  );
}
